/**
 * LiquidBubbleNav — Floating Action Button (FAB) navigator
 *
 * In UI/UX terminology this component is a "Floating Action Button" (FAB):
 * a persistent floating circular button overlay used for primary actions.
 *
 * Apple-inspired semi-transparent glass button at bottom-center.
 * Tap to expand a frosted pill-shaped menu with navigation items.
 * Features water-drop ripple effect on press (Apple liquid button style).
 *
 * Supports the same TabItem interface as BottomNavigationBar for drop-in use.
 */

import { useState, useCallback, useEffect, useMemo, useRef, type ReactNode, type MouseEvent as ReactMouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { useViewportMode } from '../contexts/ViewportModeContext';
import './LiquidBubbleNav.css';

export interface BubbleNavItem {
  /** Unique identifier */
  id: string;
  /** Display label */
  label: string;
  /** Icon element (SVG, emoji, or component) */
  icon: ReactNode;
  /** Active state icon (filled variant). Falls back to `icon` if omitted. */
  activeIcon?: ReactNode;
  /** Route path for navigation */
  route?: string;
  /** Custom click handler (overrides route navigation) */
  onClick?: () => void;
  /** Badge count or text */
  badge?: number | string;
}

export interface LiquidBubbleNavProps {
  /** Navigation items to display */
  items: BubbleNavItem[];
  /** Currently active item id */
  activeId?: string;
  /** Custom navigation handler (overrides default window.location.href) */
  onNavigate?: (item: BubbleNavItem) => void;
}

/** SVG icon: plus/cross (trigger icon) */
const MenuIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

/** SVG icon: outward arrows — "enter fullscreen / no padding". */
const ExpandIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 10V4h6" />
    <path d="M20 14v6h-6" />
    <path d="M4 4l7 7" />
    <path d="M20 20l-7-7" />
  </svg>
);

/** SVG icon: inward arrows — "exit fullscreen / restore padding". */
const CollapseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 4v6H4" />
    <path d="M14 20v-6h6" />
    <path d="M4 10l7-7" />
    <path d="M20 14l-7 7" />
  </svg>
);

/** Create a water-drop ripple element at click position */
function spawnRipple(container: HTMLElement, e: ReactMouseEvent) {
  const rect = container.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;
  const size = Math.max(rect.width, rect.height) * 2;

  const ripple = document.createElement('span');
  ripple.className = 'liquid-ripple';
  ripple.style.width = `${size}px`;
  ripple.style.height = `${size}px`;
  ripple.style.left = `${x - size / 2}px`;
  ripple.style.top = `${y - size / 2}px`;

  container.appendChild(ripple);
  ripple.addEventListener('animationend', () => ripple.remove());
}

export function LiquidBubbleNav({
  items,
  activeId,
  onNavigate,
}: LiquidBubbleNavProps) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const navRef = useRef<HTMLElement>(null);

  // Pull the viewport mode from context (gracefully no-ops when no provider
  // is mounted). The mode controls the auto-injected toggle item in the
  // FAB menu and the data-viewport-mode attribute on :root — apps that opt
  // into FAB-aware padding can hook into either.
  const viewport = useViewportMode();

  // Auto-inject a viewport-mode toggle item whenever the FAB is rendered
  // inside a ViewportModeProvider — this way every FAB across the app
  // (RootLayout's chrome, HomePage's bundle overlay, etc.) gets the toggle
  // without each call site having to wire it.
  const effectiveItems = useMemo<BubbleNavItem[]>(() => {
    if (!viewport.available) return items;
    const toggleItem: BubbleNavItem = {
      id: '__viewport_mode_toggle',
      label: viewport.mode === 'padded' ? 'Fullscreen' : 'Exit Fullscreen',
      icon: viewport.mode === 'padded' ? <ExpandIcon /> : <CollapseIcon />,
      onClick: viewport.toggleMode,
    };
    return [...items, toggleItem];
  }, [items, viewport.available, viewport.mode, viewport.toggleMode]);

  // Resolve active from route if not explicitly set
  const resolvedActiveId = activeId ?? (() => {
    const path = window.location.pathname;
    return effectiveItems.find((item) => item.route && path.startsWith(item.route))?.id;
  })();

  const handleTriggerClick = useCallback((e: ReactMouseEvent<HTMLButtonElement>) => {
    if (triggerRef.current) spawnRipple(triggerRef.current, e);
    setIsOpen((prev) => !prev);
  }, []);

  const handleItemClick = useCallback(
    (item: BubbleNavItem, e: ReactMouseEvent<HTMLButtonElement>) => {
      spawnRipple(e.currentTarget, e);
      // Slight delay so ripple is visible before navigation
      setTimeout(() => {
        setIsOpen(false);
        if (item.onClick) {
          item.onClick();
        } else if (onNavigate) {
          onNavigate(item);
        } else if (item.route) {
          window.location.href = item.route;
        }
      }, 120);
    },
    [onNavigate],
  );

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  // Prevent iOS swipe-to-close gesture within the nav area
  // Only prevent touchmove (not touchstart) to avoid blocking click events
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;

    const preventSwipe = (e: TouchEvent) => {
      e.preventDefault();
    };

    el.addEventListener('touchmove', preventSwipe, { passive: false });

    return () => {
      el.removeEventListener('touchmove', preventSwipe);
    };
  }, []);

  // Reference-counted --fab-clearance on :root. Apps that follow the standard
  // layout convention (use shared Container/Shell/Stack primitives as their
  // scroll containers) automatically pick up the FAB clearance via the CSS
  // rules in those primitives' stylesheets. Apps with custom scroll
  // containers should be refactored to use the shared primitives — see
  // CLAUDE.md "Standard App Layout".
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement as HTMLElement & {
      __fabClearanceRefs?: number;
    };
    root.__fabClearanceRefs = (root.__fabClearanceRefs ?? 0) + 1;
    root.style.setProperty(
      '--fab-clearance',
      'calc(76px + env(safe-area-inset-bottom, 0px) + 12px)',
    );
    return () => {
      root.__fabClearanceRefs = Math.max(0, (root.__fabClearanceRefs ?? 1) - 1);
      if (root.__fabClearanceRefs === 0) {
        root.style.removeProperty('--fab-clearance');
      }
    };
  }, []);

  if (effectiveItems.length === 0) return null;

  // Render at <body> level via a portal so the FAB escapes any ancestor
  // with `contain: paint` / `transform` / `filter` — those CSS properties
  // create a new containing block for position:fixed descendants, which
  // would otherwise make the FAB scroll with the inner layout instead of
  // staying pinned to the actual viewport bottom.
  if (typeof document === 'undefined') return null;

  return createPortal(
    <>
      {isOpen && (
        <div
          className="liquid-bubble-backdrop"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}
      <nav ref={navRef} className="liquid-bubble-nav" aria-label="Quick navigation">
        <div className={`liquid-bubble-menu ${isOpen ? 'liquid-bubble-menu--open' : ''}`}>
          {effectiveItems.map((item) => {
            const isActive = item.id === resolvedActiveId;
            return (
              <button
                key={item.id}
                type="button"
                className={`liquid-bubble-item ${isActive ? 'liquid-bubble-item--active' : ''}`}
                onClick={(e) => handleItemClick(item, e)}
                aria-label={item.label}
                title={item.label}
              >
                <span className="liquid-bubble-item__icon">
                  {isActive && item.activeIcon ? item.activeIcon : item.icon}
                </span>
                <span className="liquid-bubble-item__label">{item.label}</span>
                {item.badge !== undefined && (
                  <span className="liquid-bubble-item__badge" aria-label={`${item.badge} notifications`}>
                    {typeof item.badge === 'number' && item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <button
          ref={triggerRef}
          type="button"
          className={`liquid-bubble-trigger ${isOpen ? 'liquid-bubble-trigger--open' : ''}`}
          onClick={handleTriggerClick}
          aria-label={isOpen ? 'Close navigation' : 'Open navigation'}
          aria-expanded={isOpen}
        >
          <MenuIcon />
        </button>
      </nav>
    </>,
    document.body,
  );
}
